import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Pencil, Eye, Info, Power, FileDown, FileText, Receipt, Upload } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Toolbar } from '@/components/ui/Toolbar'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import type { DropdownItem } from '@/components/ui/DropdownMenu'
import { Pagination } from '@/components/ui/Pagination'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import { Select } from '@/components/ui/Select'
import { NumberInput } from '@/components/ui/NumberInput'
import { InfoPopover } from '@/components/ui/InfoPopover'
import { StatusDot } from '@/components/ui/StatusDot'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useCrudList } from '@/hooks/useCrudList'
import { useDataTableExport } from '@/hooks/useDataTableExport'
import { useStatusFilter } from '@/hooks/useStatusFilter'
import { CompaniesService, type Company, type CompanyEmployeeRow, type CompanyStatusCascade } from '@/services/modules/CompaniesService'
import { toast } from '@/components/ui/Toast'
import { extractApiError } from '@/lib/api-errors'
import { exportToXlsx, exportSheetsToXlsx, type ExportColumn } from '@/lib/exportXlsx'
import { useAuthStore, type AuthenticatedUser } from '@/stores/authStore'
import { EmployeeImportModal } from '@/components/EmployeeImportModal'
import { useAuthority } from '@/hooks/useAuthority'

const MONTHS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
].map((m, i) => ({ value: String(i + 1), label: m }))

export function CompaniesListPage() {
  const navigate = useNavigate()
  const { can } = useAuthority()
  const canCreate = can('commercial.companies.create')
  const canEdit = can('commercial.companies.edit')
  const canStatus = can('commercial.companies.status')
  const canImpersonate = can('commercial.companies.impersonate')
  const canImport = can('commercial.companies.import_employees')
  const canBilling = can('commercial.companies.generate_billing')
  const [reload, setReload] = useState(0)
  const sf = useStatusFilter({ searchPlaceholder: 'Buscar empresa' })
  const { setPage, size, setSize, rows, meta, loading } = useCrudList(CompaniesService, { size: 25, reloadKey: reload, filters: sf.filters })
  const [exportTarget, setExportTarget] = useState<Company | null>(null)
  const [billingTarget, setBillingTarget] = useState<Company | null>(null)
  const [importTarget, setImportTarget] = useState<Company | null>(null)
  // Mudança de status: guarda a empresa, o destino e a prévia do impacto
  // (null enquanto a prévia não chegou).
  const [statusTarget, setStatusTarget] = useState<
    { company: Company; next: 'ATIVO' | 'INATIVO'; preview: CompanyStatusCascade | null } | null
  >(null)
  const [statusBusy, setStatusBusy] = useState(false)

  function refresh() { setReload(v => v + 1) }

  const exp = useDataTableExport({
    filename: 'empresas',
    service: CompaniesService,
    filters: sf.filters,
    columns: [
      { header: 'Nome',         accessor: (c: Company) => c.name },
      { header: 'Razão social', accessor: (c: Company) => c.corporateName ?? '' },
      { header: 'CPF/CNPJ',     accessor: (c: Company) => c.formatted?.cpfCnpj ?? c.cpfCnpj },
      { header: 'E-mail',       accessor: (c: Company) => c.email ?? '' },
      { header: 'Telefone',     accessor: (c: Company) => c.formatted?.phone ?? c.phone ?? '' },
      { header: 'Celular',      accessor: (c: Company) => c.formatted?.mobile ?? c.mobile ?? '' },
      { header: 'Vencimento',   accessor: (c: Company) => `dia ${c.dueDay}` },
      { header: 'Status',       accessor: (c: Company) => c.status },
    ],
  })

  const startImpersonation = useAuthStore(s => s.startImpersonation)
  async function handleView(c: Company) {
    try {
      const data = await CompaniesService.impersonate(c.id)
      const { accessToken, ...user } = data
      startImpersonation(user as unknown as AuthenticatedUser, accessToken, c.name)
      toast.success({ title: 'Visualizando empresa', description: c.name })
      navigate('/my-company/dashboard')
    } catch (e) {
      toast.error({ title: 'Não foi possível abrir o painel', description: extractApiError(e).message })
    }
  }

  // Mudar o status de uma empresa mexe na carteira inteira dela, então a tela
  // pergunta antes mostrando quantas pessoas seriam afetadas.
  async function askToggleStatus(c: Company) {
    const next: 'ATIVO' | 'INATIVO' = c.status === 'ATIVO' ? 'INATIVO' : 'ATIVO'
    setStatusTarget({ company: c, next, preview: null })
    try {
      const preview = await CompaniesService.statusPreview(c.id, next)
      setStatusTarget(t => (t && t.company.id === c.id ? { ...t, preview } : t))
    } catch {
      // Sem a prévia o diálogo ainda funciona — só não mostra os números.
      setStatusTarget(t => (t && t.company.id === c.id ? { ...t, preview: { holders: 0, dependents: 0 } } : t))
    }
  }

  async function confirmToggleStatus() {
    if (!statusTarget) return
    const { company: c, next } = statusTarget
    setStatusBusy(true)
    try {
      const { holders, dependents } = await CompaniesService.setStatusCascading(c.id, next)
      const verbo = next === 'INATIVO' ? 'inativados' : 'reativados'
      const juntos = holders === 0 && dependents === 0
        ? ''
        : ` ${holders} colaborador(es) e ${dependents} dependente(s) ${verbo}.`
      toast.success({ title: 'Status atualizado', description: `${c.name} agora está ${next}.${juntos}` })
      setStatusTarget(null)
      refresh()
    } catch (e) {
      toast.error({ title: 'Não foi possível alterar o status', description: extractApiError(e).message })
    } finally { setStatusBusy(false) }
  }

  const columns: DataTableColumn<Company>[] = [
    { key: 'name', header: 'Nome', sortable: true, alwaysVisible: true, className: 'w-full', render: c => (
      <div className="flex items-center gap-3">
        <InfoPopover align="left" ariaLabel="Detalhes da empresa" title="Detalhes"
          trigger={<Info size={16} className="text-gray-400 hover:text-brand-500 transition" />}>
          <dl className="space-y-2 text-xs">
            <InfoRow label="Colaboradores" value={String(c.titularesCount ?? 0)} />
            <InfoRow label="Gestor" value={c.manager?.name ?? '—'} />
            <InfoRow label="Cadastrada em" value={c.formatted?.createdAt ?? '—'} />
            <InfoRow label="Atualizada em" value={c.formatted?.updatedAt ?? '—'} />
          </dl>
        </InfoPopover>
        <div className="min-w-0">
          <div className="font-medium text-gray-800">{c.name}</div>
          {c.corporateName && <div className="text-[11px] text-gray-500">{c.corporateName}</div>}
        </div>
      </div>
    )},
    { key: 'cpfCnpj', header: 'CPF/CNPJ', align: 'center', render: c => <span className="text-xs">{c.formatted?.cpfCnpj ?? c.cpfCnpj}</span>, className: 'w-44 whitespace-nowrap' },
    { key: 'contact', header: 'Contato', align: 'center', className: 'whitespace-nowrap', render: c => (
      <div className="text-xs text-gray-500">
        {c.formatted?.mobile ?? c.formatted?.phone ?? '—'}
        {c.email && <div className="text-[11px] text-gray-400">{c.email}</div>}
      </div>
    )},
    { key: 'dueDay', header: 'Vencimento', align: 'center', render: c => `dia ${c.dueDay}`, className: 'w-28' },
    { key: 'status', header: 'Status', align: 'center', className: 'w-24', render: c => <StatusDot value={c.status} /> },
  ]

  function rowActions(c: Company): DropdownItem[] {
    const head: DropdownItem[] = [
      ...(canImpersonate ? [{ key: 'view', label: 'Visualizar painel', icon: <Eye size={14} />, onClick: () => handleView(c) }] : []),
      ...(canEdit ? [{ key: 'edit', label: 'Editar', icon: <Pencil size={14} />, onClick: () => navigate(`/commercial/companies/${c.id}/editar`) }] : []),
    ]
    const items: DropdownItem[] = [
      ...head,
      ...(canImport ? [{ key: 'import', label: 'Importar funcionários', icon: <Upload size={14} />, divider: head.length > 0, onClick: () => setImportTarget(c) }] : []),
      { key: 'export', label: 'Exportar funcionários', icon: <FileDown size={14} />, divider: head.length > 0 && !canImport, onClick: () => setExportTarget(c) },
      ...(canBilling ? [{ key: 'billing', label: 'Gerar faturamento', icon: <Receipt size={14} />, onClick: () => setBillingTarget(c) }] : []),
      { key: 'invoices', label: 'Faturas', icon: <FileText size={14} />, onClick: () => navigate(`/commercial/companies/${c.id}/faturas`) },
    ]
    if (canStatus) {
      items.push({
        key: 'status',
        label: c.status === 'ATIVO' ? 'Inativar' : 'Ativar',
        icon: <Power size={14} />,
        divider: true,
        danger: c.status === 'ATIVO',
        onClick: () => void askToggleStatus(c),
      })
    }
    return items
  }

  return (
    <>
      <PageHeader
        title="Empresas"
        subtitle="Empresas clientes (PJ) com planos coletivos"
        crumbs={[{ label: 'Comercial' }, { label: 'Empresas' }]}
        actions={canCreate ? <Button onClick={() => navigate('/commercial/companies/novo')} iconLeft={<Plus size={14} />}>Nova empresa</Button> : undefined}
      />
      <Toolbar onFiltersClick={sf.open} activeFiltersCount={sf.activeCount} />
      <DataTable
        tableId="companies.list"
        exportable
        onExportPage={() => exp.exportPage(rows)}
        onExportAll={exp.exportAll}
        columns={columns}
        rows={rows}
        loading={loading}
        getRowKey={r => r.id}
        rowActions={rowActions}
      />
      <Pagination meta={meta} onChange={setPage} size={size} onSizeChange={setSize} />

      {exportTarget && (
        <ExportEmployeesModal company={exportTarget} onClose={() => setExportTarget(null)} />
      )}
      {billingTarget && (
        <GenerateBillingModal company={billingTarget} onClose={() => setBillingTarget(null)} onDone={() => { setBillingTarget(null); refresh() }} />
      )}
      {importTarget && (
        <EmployeeImportModal
          title={`Importar funcionários — ${importTarget.name}`}
          onClose={() => setImportTarget(null)}
          onImported={refresh}
          downloadTemplate={() => CompaniesService.employeesImportTemplate(importTarget.id)}
          preview={(f) => CompaniesService.employeesImportPreview(importTarget.id, f)}
          confirm={(f) => CompaniesService.employeesImportConfirm(importTarget.id, f)}
        />
      )}
      <ConfirmDialog
        open={!!statusTarget}
        onClose={() => setStatusTarget(null)}
        loading={statusBusy}
        variant={statusTarget?.next === 'INATIVO' ? 'danger' : 'primary'}
        confirmLabel={statusTarget?.next === 'INATIVO' ? 'Inativar tudo' : 'Reativar'}
        title={statusTarget?.next === 'INATIVO'
          ? `Inativar ${statusTarget?.company.name}?`
          : `Reativar ${statusTarget?.company.name}?`}
        description={statusDescription(statusTarget)}
        onConfirm={() => void confirmToggleStatus()}
      />

      {sf.drawer}
    </>
  )
}

/** Texto do diálogo — depende do sentido da mudança e da prévia carregada. */
function statusDescription(
  t: { next: 'ATIVO' | 'INATIVO'; preview: CompanyStatusCascade | null } | null,
): string {
  if (!t) return ''
  if (!t.preview) return 'Calculando quantas pessoas seriam afetadas…'

  const { holders, dependents } = t.preview
  const pessoas = `${holders} colaborador(es) e ${dependents} dependente(s)`

  if (t.next === 'INATIVO') {
    return holders === 0 && dependents === 0
      ? 'Nenhum colaborador ativo será afetado.'
      : `${pessoas} serão inativados junto com a empresa. Quem já estava inativo continua como está.`
  }
  return holders === 0 && dependents === 0
    ? 'Nenhum colaborador voltará automaticamente — os inativos foram desligados individualmente.'
    : `${pessoas} voltam a ficar ativos. Quem foi desligado individualmente continua inativo.`
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-gray-500">{label}</dt>
      <dd className="font-medium text-gray-800 text-right">{value}</dd>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Exportar funcionários — passo 1 (incluir) → passo 2 (agrupar, se houver filiais)
// ---------------------------------------------------------------------------
const BASE_COLS: ExportColumn<CompanyEmployeeRow>[] = [
  { header: 'Tipo',    accessor: r => r.type },
  { header: 'Nome',    accessor: r => r.name },
  { header: 'CPF',     accessor: r => r.cpf ?? '' },
  { header: 'Carteirinha', accessor: r => r.card ?? '' },
  { header: 'Data de nascimento', accessor: r => r.birthDate ?? '' },
  { header: 'Plano',   accessor: r => r.plan ?? '' },
  { header: 'Titular', accessor: r => r.holderName ?? '' },
  { header: 'Status',  accessor: r => r.status },
]

function ExportEmployeesModal({ company, onClose }: { company: Company; onClose: () => void }) {
  const [include, setInclude] = useState<'holders' | 'holders_dependents'>('holders')
  const [step, setStep] = useState<1 | 2>(1)
  const [rows, setRows] = useState<CompanyEmployeeRow[]>([])
  const [busy, setBusy] = useState(false)

  function downloadUnified(data: CompanyEmployeeRow[]) {
    if (data.length === 0) { toast.error({ title: 'Nada a exportar', description: 'A empresa não tem funcionários para o filtro escolhido.' }); return }
    exportToXlsx(`funcionarios-${company.name}`, data, [{ header: 'Filial', accessor: r => r.branch ?? 'Sem filial' }, ...BASE_COLS])
    onClose()
  }
  function downloadByBranch(data: CompanyEmployeeRow[]) {
    const groups = new Map<string, CompanyEmployeeRow[]>()
    for (const r of data) {
      const key = r.branch ?? 'Sem filial'
      if (!groups.has(key)) groups.set(key, [])
      groups.get(key)!.push(r)
    }
    exportSheetsToXlsx(`funcionarios-${company.name}`, [...groups.entries()].map(([name, gr]) => ({ name, rows: gr, columns: BASE_COLS })))
    onClose()
  }

  async function continueStep1() {
    setBusy(true)
    try {
      const res = await CompaniesService.employeesExportData(company.id, include)
      setRows(res.data)
      if (res.hasBranches) {
        setStep(2)               // empresa com filiais → pergunta agrupamento
      } else {
        downloadUnified(res.data) // sem filiais → exporta direto
      }
    } catch (e) {
      toast.error({ title: 'Erro ao exportar', description: extractApiError(e).message })
    } finally { setBusy(false) }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Exportar funcionários — ${company.name}`}
      size="sm"
      footer={step === 1 ? (
        <>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={continueStep1} loading={busy}>Continuar</Button>
        </>
      ) : (
        <>
          <Button variant="ghost" onClick={() => setStep(1)}>Voltar</Button>
        </>
      )}
    >
      {step === 1 ? (
        <div className="space-y-3">
          <p className="text-sm text-gray-600">O que deseja incluir na exportação?</p>
          <ChoiceButton active={include === 'holders'} onClick={() => setInclude('holders')}
            title="Somente funcionários" desc="Apenas os titulares (colaboradores)." />
          <ChoiceButton active={include === 'holders_dependents'} onClick={() => setInclude('holders_dependents')}
            title="Funcionários e dependentes" desc="Titulares + seus dependentes." />
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-gray-600">A empresa tem filiais. Como deseja organizar a planilha?</p>
          <ChoiceButton active={false} onClick={() => downloadByBranch(rows)}
            title="Separado por filiais" desc="Uma aba para cada filial." />
          <ChoiceButton active={false} onClick={() => downloadUnified(rows)}
            title="Unificado" desc="Uma única aba com a coluna Filial." />
        </div>
      )}
    </Modal>
  )
}

function ChoiceButton({ active, onClick, title, desc }: { active: boolean; onClick: () => void; title: string; desc: string }) {
  return (
    <button type="button" onClick={onClick}
      className={`w-full cursor-pointer rounded-md px-3 py-2.5 text-left ring-1 transition ${
        active ? 'bg-brand-50 ring-brand-300' : 'bg-white ring-gray-200 hover:bg-gray-50'
      }`}>
      <div className="text-sm font-medium text-gray-800">{title}</div>
      <div className="text-xs text-gray-500">{desc}</div>
    </button>
  )
}

// ---------------------------------------------------------------------------
// Gerar faturamento — escolhe mês/ano e gera a fatura agregada da empresa.
// ---------------------------------------------------------------------------
function GenerateBillingModal({ company, onClose, onDone }: { company: Company; onClose: () => void; onDone: () => void }) {
  const now = new Date()
  const [month, setMonth] = useState(String(now.getMonth() + 1))
  const [year, setYear] = useState<number | null>(now.getFullYear())
  const [saving, setSaving] = useState(false)

  async function save() {
    if (!year) { toast.error({ title: 'Ano obrigatório', description: 'Informe o ano.' }); return }
    setSaving(true)
    try {
      const res = await CompaniesService.generateBilling(company.id, Number(month), year)
      toast.success({ title: 'Faturamento gerado', description: `${res.collaborators} colaborador(es) faturado(s).` })
      onDone()
    } catch (e) {
      toast.error({ title: 'Não foi possível gerar', description: extractApiError(e).message })
    } finally { setSaving(false) }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Gerar faturamento — ${company.name}`}
      size="sm"
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancelar</Button>
        <Button onClick={save} loading={saving}>Gerar</Button>
      </>}
    >
      <div className="space-y-3">
        <p className="text-sm text-gray-600">
          Gera a fatura do mês com base nos colaboradores e planos ativos da empresa. Não é permitido duplicar a competência (mês/ano).
        </p>
        <div className="grid grid-cols-2 gap-3">
          <Select label="Mês" value={month} onChange={(e) => setMonth(e.target.value)} options={MONTHS} searchable={false} clearable={false} />
          <NumberInput label="Ano" value={year} onChange={setYear} decimals={0} min={2020} max={2100} />
        </div>
      </div>
    </Modal>
  )
}
